import { useEffect, useMemo, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Link } from "react-router-dom";
import { assets } from "../lib/assets";
import { GACHA_REWARDS } from "../services/surprise/surpriseRepository";
import {
  equipInventoryItem,
  loadPlayerCollection,
  purchaseCatalogItem,
  rewardDefinition,
  saveRoomPlacements,
  type PlayerCollection,
  type RoomPlacement
} from "../services/economy/economyRepository";
import { useAppStore } from "../store/useAppStore";

const emptyCollection: PlayerCollection = { inventory: [], placements: [], equipped: {}, source: "local" };
const itemVisuals: Record<string, string> = {
  "star-cushion": assets.starCushion,
  "moon-plant": assets.moonPlant,
  "moon-lamp": assets.starLantern,
  "cloud-bunny": assets.cloudBunny,
  "toy-telescope": assets.gachaRewardTelescope,
  "starlight-dress": assets.gachaRewardDress,
  "rainbow-cape": assets.gachaRewardDress
};
const itemCost = (rarity: string) => rarity === "magic" ? 58 : rarity === "rare" ? 28 : 12;

export function RoomPage() {
  const { activeChildId, childName, stars, setStars } = useAppStore();
  const [collection, setCollection] = useState<PlayerCollection>(emptyCollection);
  const [fish, setFish] = useState(3);
  const [treats, setTreats] = useState(2);
  const [decorating, setDecorating] = useState(false);
  const [panel, setPanel] = useState<"wardrobe" | "shop" | "feed-lumi" | "feed-aduo" | null>(null);
  const [message, setMessage] = useState("点一点家具和伙伴，看看会发生什么。");

  useEffect(() => {
    let alive = true;
    void loadPlayerCollection(activeChildId).then((next) => { if (alive) setCollection(next); });
    return () => { alive = false; };
  }, [activeChildId]);

  const ownedIds = useMemo(() => new Set(collection.inventory.map((item) => item.rewardId)), [collection.inventory]);
  const placeable = collection.inventory.filter((item) => item.itemType === "room_decor" || item.itemType === "toy");
  const wearable = collection.inventory.filter((item) => item.itemType === "clothing");
  const shopItems = GACHA_REWARDS.filter((item) => item.type !== "stars" && !ownedIds.has(item.id));

  const persist = (next: PlayerCollection) => {
    setCollection(next);
    void saveRoomPlacements(activeChildId, next);
  };
  const addItem = (rewardId: string) => {
    const next: RoomPlacement = { id: crypto.randomUUID(), rewardId, x: 48 + Math.random() * 18, y: 66 + Math.random() * 10, scale: 1, layer: collection.placements.length + 1 };
    persist({ ...collection, placements: [...collection.placements, next] });
    setMessage(`${rewardDefinition(rewardId)?.name ?? "收藏品"}已经摆进房间，可以拖动调整位置。`);
  };
  const removeItem = (id: string) => persist({ ...collection, placements: collection.placements.filter((item) => item.id !== id) });
  const moveItem = (event: ReactPointerEvent<HTMLDivElement>, id: string) => {
    if (!decorating || !event.currentTarget.hasPointerCapture(event.pointerId)) return;
    const stage = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!stage) return;
    const x = Math.max(5, Math.min(94, (event.clientX - stage.left) / stage.width * 100));
    const y = Math.max(20, Math.min(88, (event.clientY - stage.top) / stage.height * 100));
    persist({ ...collection, placements: collection.placements.map((item) => item.id === id ? { ...item, x, y } : item) });
  };
  const feed = (pet: "lumi" | "aduo", food: "fish" | "treats") => {
    const amount = food === "fish" ? fish : treats;
    if (amount <= 0) return setMessage("点心吃完啦，完成探索可以带回更多。");
    if (food === "fish") setFish((value) => value - 1); else setTreats((value) => value - 1);
    setPanel(null);
    setMessage(pet === "aduo" ? "阿朵开心地眯起眼睛，尾巴上的星星亮了！" : "露米吃饱啦，星芽发出了暖暖的光。");
  };
  const equip = async (rewardId: string) => {
    const slot = String(rewardDefinition(rewardId)?.type === "clothing" ? "outfit" : "accessory");
    try {
      const next = await equipInventoryItem(activeChildId, collection, slot, rewardId);
      setCollection(next);
      setMessage(`${rewardDefinition(rewardId)?.name ?? "服装"}已经穿戴，下次角色换装会继续使用。`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "暂时无法穿戴。 "); }
  };
  const purchase = async (rewardId: string) => {
    try {
      const result = await purchaseCatalogItem(activeChildId, collection, stars, rewardId);
      setCollection(result.collection);
      setStars(result.totalStars);
      setMessage(`${rewardDefinition(rewardId)?.name ?? "收藏品"}已经收入我的收藏。`);
    } catch (error) { setMessage(error instanceof Error ? error.message : "兑换没有成功，请稍后再试。 "); }
  };

  return <section className="react-room page-view">
    <header>
      <Link to="/" aria-label="返回首页">‹</Link>
      <div><p>我的小屋 · 收藏中心</p><h1>欢迎回到<br />{childName}的星光房间</h1><span>任务、免费扭蛋和星光兑换得到的物品，都保存到同一份收藏。</span></div>
      <aside><span>✦ <b>{stars}</b></span><span>收藏 <b>{collection.inventory.length}</b></span><button onClick={() => setPanel("shop")}>星光商店</button><button onClick={() => setDecorating((value) => !value)}>{decorating ? "完成布置 ✓" : "布置房间 ＋"}</button></aside>
    </header>
    <main className={`react-room-stage ${decorating ? "decorating" : ""}`}>
      <img className="room-bg" src="/assets/rooms/princess-room-c4d-v1.webp" alt="星光公主房" />
      <button className="room-zone bed" onClick={() => setMessage("掀开星星被子，听一段晚安故事吧。")}>睡觉</button>
      <button className="room-zone desk" onClick={() => setMessage("书桌上的绘本打开啦！")}>读绘本</button>
      <button className="room-zone wardrobe" onClick={() => setPanel("wardrobe")}>打开衣柜</button>
      <button className="room-pet lumi" onClick={() => setPanel("feed-lumi")}><img src={assets.lumi} alt="露米" /></button>
      <button className="room-pet aduo" onClick={() => setPanel("feed-aduo")}><img src={assets.aduo} alt="阿朵" /></button>
      {collection.placements.map((item) => {
        const meta = rewardDefinition(item.rewardId);
        const image = itemVisuals[item.rewardId];
        if (!meta || !image) return null;
        return <div className="room-placed" key={item.id} style={{ left: `${item.x}%`, top: `${item.y}%`, zIndex: 5 + item.layer, transform: `translate(-50%,-50%) scale(${item.scale})` }} onPointerDown={(event) => { if (decorating) event.currentTarget.setPointerCapture(event.pointerId); }} onPointerMove={(event) => moveItem(event, item.id)} onPointerUp={(event) => { if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId); }}><img src={image} alt={meta.name} />{decorating && <button onClick={() => removeItem(item.id)}>×</button>}</div>;
      })}
      {decorating && <aside className="room-dock"><b>我的家具</b>{placeable.length ? placeable.map((entry) => { const meta = rewardDefinition(entry.rewardId); return <button key={entry.rewardId} onClick={() => addItem(entry.rewardId)}><img src={itemVisuals[entry.rewardId]} alt="" /><small>{meta?.name}</small></button>; }) : <span>去扭蛋机收集第一件家具吧</span>}</aside>}
      <div className="room-message">{message}</div>
    </main>
    {panel && <div className="room-modal" onClick={(event) => { if (event.target === event.currentTarget) setPanel(null); }}><article><button className="close" onClick={() => setPanel(null)}>×</button>
      {panel === "wardrobe" ? <><p>我的收藏</p><h2>星光衣柜</h2><span className="collection-tip">扭蛋或星光兑换获得的服装会出现在这里，点一下即可穿戴。</span><div className="room-collection">{wearable.length ? wearable.map((entry) => { const meta = rewardDefinition(entry.rewardId); const active = collection.equipped.outfit === entry.rewardId; return <button className={active ? "is-equipped" : ""} key={entry.rewardId} onClick={() => void equip(entry.rewardId)}><img src={itemVisuals[entry.rewardId]} alt="" /><b>{meta?.name}</b><small>{active ? "已穿戴" : "点击穿戴"}</small></button>; }) : <div className="collection-empty">还没有服装，今日扭蛋和星光商店里都可能遇见。</div>}</div></>
      : panel === "shop" ? <><p>只兑换装扮，不影响学习进度</p><h2>星光商店</h2><span className="collection-tip">普通 12、稀有 28、魔法 58 颗星光；已拥有的物品不会重复购买。</span><div className="room-shop">{shopItems.map((item) => <button key={item.id} onClick={() => void purchase(item.id)} disabled={stars < itemCost(item.rarity)}><img src={itemVisuals[item.id]} alt="" /><span><b>{item.name}</b><small>{item.rarity === "magic" ? "魔法" : item.rarity === "rare" ? "稀有" : "普通"} · ✦ {itemCost(item.rarity)}</small></span></button>)}</div></>
      : <><p>宠物照料</p><h2>给{panel === "feed-aduo" ? "阿朵" : "露米"}喂点心</h2><div className="room-feed"><button onClick={() => feed(panel === "feed-aduo" ? "aduo" : "lumi", "fish")}>🐟<b>星星小鱼 · {fish}</b></button><button onClick={() => feed(panel === "feed-aduo" ? "aduo" : "lumi", "treats")}>🍪<b>月亮饼干 · {treats}</b></button></div></>}
    </article></div>}
  </section>;
}